import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Box, 
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs
} from '@mui/material';
import { 
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import api from '../services/api';

const SocialMediaMonitor = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/social/dashboard');
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncChannel = async (channelId) => {
    try {
      setSyncing({ ...syncing, [channelId]: true });
      await api.post(`/social/channels/${channelId}/sync`);
      fetchDashboard();
    } catch (error) {
      console.error('Error syncing channel:', error);
    } finally {
      setSyncing({ ...syncing, [channelId]: false });
    }
  };

  const handleResolveAlert = async () => {
    try {
      await api.put(`/social/alerts/${selectedAlert.id}/resolve`, {
        resolvedBy: 'Admin',
        resolutionNotes
      });
      setResolveDialogOpen(false);
      setResolutionNotes('');
      fetchDashboard();
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await api.put(`/social/alerts/${alertId}/dismiss`);
      fetchDashboard();
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const getPlatformIcon = (platform) => {
    return platform === 'youtube' ? <YouTubeIcon /> : <FacebookIcon />;
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ErrorIcon color="error" />;
      case 'warning': return <WarningIcon color="warning" />;
      case 'info': return <InfoIcon color="info" />;
      default: return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">No data available</Alert>
      </Container>
    );
  }

  const { channels, recentVideos, alerts, summary } = dashboardData;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          📱 Media Monitor
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchDashboard}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={1} mb={2}>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Total Channels
              </Typography>
              <Typography variant="h4" fontWeight={700}>{summary.totalChannels}</Typography>
              <Typography variant="caption" color="textSecondary">
                Active monitoring
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Recent Videos
              </Typography>
              <Typography variant="h4" fontWeight={700}>{summary.totalVideos}</Typography>
              <Typography variant="caption" color="textSecondary">
                Last 20 uploads
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Active Alerts
              </Typography>
              <Typography variant="h4" fontWeight={700}>{summary.totalAlerts}</Typography>
              <Typography variant="caption" color="textSecondary">
                Requiring attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Channel Cards */}
      <Typography variant="h5" gutterBottom>
        Channels
      </Typography>
      <Grid container spacing={3} mb={4}>
        {channels.map((channel) => (
          <Grid item xs={12} sm={6} md={3} key={channel.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  {getPlatformIcon(channel.platform)}
                  <Chip 
                    label={channel.syncStatus} 
                    color={channel.syncStatus === 'success' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                
                <Typography variant="h6" gutterBottom>
                  {channel.channelName}
                </Typography>
                
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {channel.platformChannelName || 'Not configured'}
                </Typography>

                <Box mt={2}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Videos
                      </Typography>
                      <Typography variant="body1">
                        {channel.stats?.totalVideos || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Views
                      </Typography>
                      <Typography variant="body1">
                        {formatNumber(channel.stats?.totalViews || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {channel.alerts && channel.alerts.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {channel.alerts.length} active alert(s)
                  </Alert>
                )}

                <Box mt={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={syncing[channel.id] ? <CircularProgress size={20} /> : <SyncIcon />}
                    onClick={() => handleSyncChannel(channel.id)}
                    disabled={syncing[channel.id] || !channel.isActive}
                  >
                    {syncing[channel.id] ? 'Syncing...' : 'Sync Now'}
                  </Button>
                </Box>

                {channel.lastSyncAt && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Last synced: {new Date(channel.lastSyncAt).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs for Videos and Alerts */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="Recent Videos" />
          <Tab label={`Alerts (${summary.totalAlerts})`} />
        </Tabs>
      </Box>

      {/* Recent Videos Tab */}
      {selectedTab === 0 && (
        <Grid container spacing={2}>
          {recentVideos.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">No videos available. Sync channels to fetch video data.</Alert>
            </Grid>
          ) : (
            recentVideos.map((video) => (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <Card>
                  {video.thumbnailUrl && (
                    <Box
                      component="img"
                      src={video.thumbnailUrl}
                      alt={video.videoTitle}
                      sx={{ width: '100%', height: 180, objectFit: 'cover' }}
                    />
                  )}
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom noWrap>
                      {video.videoTitle}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                      {getPlatformIcon(video.platform)}
                      <Typography variant="caption" color="textSecondary">
                        {video.channel?.channelName}
                      </Typography>
                    </Box>

                    <Grid container spacing={1} mb={1}>
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <VisibilityIcon fontSize="small" color="action" />
                          <Typography variant="caption">
                            {formatNumber(video.viewCount)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <ThumbUpIcon fontSize="small" color="action" />
                          <Typography variant="caption">
                            {formatNumber(video.likeCount)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CommentIcon fontSize="small" color="action" />
                          <Typography variant="caption">
                            {formatNumber(video.commentCount)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Chip 
                      label={video.uploadStatus} 
                      size="small" 
                      color={video.uploadStatus === 'published' ? 'success' : 'default'}
                    />

                    {video.videoUrl && (
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        href={video.videoUrl}
                        target="_blank"
                      >
                        View Video
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Alerts Tab */}
      {selectedTab === 1 && (
        <Grid container spacing={2}>
          {['critical', 'warning', 'info'].map((severity) => {
            const severityAlerts = alerts[severity] || [];
            if (severityAlerts.length === 0) return null;

            return (
              <Grid item xs={12} key={severity}>
                <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>
                  {severity} Alerts ({severityAlerts.length})
                </Typography>
                <Grid container spacing={2}>
                  {severityAlerts.map((alert) => (
                    <Grid item xs={12} md={6} key={alert.id}>
                      <Card>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box display="flex" gap={1} flex={1}>
                              {getSeverityIcon(alert.severity)}
                              <Box>
                                <Typography variant="subtitle1" gutterBottom>
                                  {alert.title}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" paragraph>
                                  {alert.message}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Detected: {formatDate(alert.detectedAt)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {alert.channel && (
                            <Chip 
                              label={`${alert.channel.channelName} - ${alert.channel.platform}`}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}

                          {alert.video && (
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              Video: {alert.video.videoTitle}
                            </Typography>
                          )}

                          <Box display="flex" gap={1} mt={2}>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => {
                                setSelectedAlert(alert);
                                setResolveDialogOpen(true);
                              }}
                            >
                              Resolve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleDismissAlert(alert.id)}
                            >
                              Dismiss
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            );
          })}

          {summary.totalAlerts === 0 && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<CheckCircleIcon />}>
                No active alerts. All systems running smoothly!
              </Alert>
            </Grid>
          )}
        </Grid>
      )}

      {/* Resolve Alert Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Alert</DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {selectedAlert.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {selectedAlert.message}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Resolution Notes"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe how this issue was resolved..."
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResolveAlert} variant="contained" color="primary">
            Mark as Resolved
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SocialMediaMonitor;
